import { Link, Navigate, useParams } from "react-router-dom";
import {
  formatNewsDate,
  getNewsArchivePage,
  getNewsArchivePageCount,
  getNewsPostPath,
} from "../news/posts";

function parsePageParam(pageParam: string | undefined) {
  if (!pageParam) {
    return 1;
  }

  const pageNumber = Number(pageParam);
  return Number.isInteger(pageNumber) && pageNumber > 0 ? pageNumber : undefined;
}

function archivePath(pageNumber: number) {
  return pageNumber === 1 ? "/posts" : `/posts/page/${pageNumber}`;
}

export function NewsArchivePage() {
  const { pageNum } = useParams();
  const pageNumber = parsePageParam(pageNum);
  const pageCount = getNewsArchivePageCount();

  if (!pageNumber || pageNumber > pageCount) {
    return <Navigate to="/posts" replace />;
  }

  const posts = getNewsArchivePage(pageNumber);

  return (
    <>
      <section className="page-hero">
        <div className="content-wrapper py-4">
          <h1 className="display-6 fw-semibold mb-3">Posts & Updates</h1>
          <p className="fs-3 mb-0">
            Site news, release notes, community announcements, and Winter's musings.
          </p>
        </div>
      </section>

      <div className="content-wrapper py-4">
        <div className="news-archive">
          {posts.length > 0 ? (
            posts.map((post) => (
              <article className="news-card border-radius-0" key={`${post.date}-${post.slug}`}>
                <time className="news-card__date" dateTime={post.date}>
                  {formatNewsDate(post.date)}
                </time>
                <h2 className="h3 mb-3">
                  <Link to={getNewsPostPath(post)}>{post.title}</Link>
                </h2>
                <p className="mb-0 text-body-secondary">{post.excerpt}</p>
              </article>
            ))
          ) : (
            <div className="card border-radius-0">
              <div className="card-body">
                <p className="mb-0 text-body-secondary">No posts yet.</p>
              </div>
            </div>
          )}
        </div>

        {pageCount > 1 ? (
          <nav className="news-pagination" aria-label="News archive pages">
            <ul className="pagination mb-0">
              <li className={`page-item ${pageNumber === 1 ? "disabled" : ""}`}>
                <Link
                  className="page-link"
                  to={archivePath(Math.max(1, pageNumber - 1))}
                  aria-disabled={pageNumber === 1}
                >
                  Previous
                </Link>
              </li>
              {Array.from({ length: pageCount }, (_, index) => index + 1).map(
                (page) => (
                  <li
                    className={`page-item ${page === pageNumber ? "active" : ""}`}
                    key={page}
                  >
                    <Link
                      className="page-link"
                      to={archivePath(page)}
                      aria-current={page === pageNumber ? "page" : undefined}
                    >
                      {page}
                    </Link>
                  </li>
                ),
              )}
              <li
                className={`page-item ${pageNumber === pageCount ? "disabled" : ""}`}
              >
                <Link
                  className="page-link"
                  to={archivePath(Math.min(pageCount, pageNumber + 1))}
                  aria-disabled={pageNumber === pageCount}
                >
                  Next
                </Link>
              </li>
            </ul>
          </nav>
        ) : null}
      </div>
    </>
  );
}
